<template>
    <Transition name="sp-modal">
        <div
            ref="severityChange"
            tabindex="0"
            class="sp-modal-mask"
            @keydown.esc="$emit('close')"
        >
            <div class="sp-modal-wrapper">
                <div class="sp-modal-container">
                    <div class="sp-modal-header">
                        <span class="sp-modal-header-title">{{ severityChange.cve_name }}</span>
                        <span
                            tabindex="0"
                            class="sp-modal-header-closebtn material-symbols-outlined"
                            @click="$emit('close')"
                        >Close</span>
                    </div>
                    <div class="sp-modal-content">
                        <div class="sp-modal-severity">
                            <span>
                                {{ severityChange.componentDetails?.component }}
                                <span v-if="severityChange.componentDetails?.version">
                                    {{ severityChange.componentDetails?.version }}
                                </span>
                                <span v-else-if="isComponentLevelChange" class="all-versions-indicator">
                                    (All Versions)
                                </span>
                            </span>
                            <v-select
                                v-model="miraSeverity"
                                :options="$parent.$parent.severity_list"
                                :searchable="false"
                                label="name"
                                placeholder="Select severity"
                            />
                        </div>
                            <div v-if="!isComponentLevelChange" class="sp-modal-artifact-severity">
                                <span>
                                    {{ severityChange?.component?.image?.name }}<br>
                                    (<i>{{ severityChange?.component?.artifact_id }}</i>)
                                </span>
                            </div>
                            <div v-else class="sp-modal-component-info">
                                <span>
                                    This will update severity for all {{ allComponentIdsCount }} version(s) of this component
                                    and all their artifacts.
                                </span>
                            </div>
                        <input
                            v-show="miraSeverity !== null"
                            v-model="miraCVSS3Vector"
                            type="text"
                            placeholder="CVSS 3.0 vector (optional)"
                        >
                        <textarea
                            v-show="miraSeverity !== null"
                            v-model="miraComment"
                            type="text"
                            placeholder="Reason to change"
                        />
                        <div class="sp-modal-submit">
                            <div>
                                <span v-if="miraSeverity !== null && miraComment == ''">
                                    Reason required
                                </span>
                            </div>
                            <input
                                :disabled="miraSeverity !== null && miraComment == ''"
                                type="submit"
                                :value="miraSeverity === null ? 'Reset to upstream' : 'Update severity'"
                                @click="updateSeverity"
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import vSelect from 'vue-select'

export default {
    name: 'SeverityChange',
    components: {
        vSelect,
    },
    data() {
        return {
            miraSeverity: this.severityChange?.component?.mira_severity?.id
                ? this.$parent.$parent.severity_list?.filter((el) => {
                    return el?.id == this.severityChange.component.mira_severity.id
                })[0] || null
                : null,
            miraComment: this.severityChange?.component?.mira_comment || '',
            miraCVSS3Vector: this.severityChange?.component?.mira_cvss3_vector || '',
        }
    },
    props: {
        severityChange: {
            type: Object,
            default: null,
        },
    },
    emits: ['close'],

    computed: {
        isComponentLevelChange() {
            // Check if this is a component-level change (affects all versions)
            return this.severityChange?.dataType === 'component' &&
                   this.severityChange?.componentDetails?.version === null &&
                   this.severityChange?.componentDetails?.allComponentIds?.length > 0;
        },
        allComponentIdsCount() {
            return this.severityChange?.componentDetails?.allComponentIds?.length || 0;
        },
        cveInfoSeverity() { return this.$store.state.cveInfo.severity || 'N/A' },
        severityStyle() {
            const severity = this.$store.state.cveInfo.severity || 'none'
            return { 'border-left-color': `var(--color-severity-${severity.toLowerCase()})` }
        },
        severityLabelClass() {
            const severity = this.$store.state.cveInfo.severity || 'none'
            return ['sp-severity-label', `sp-severity-label-${severity.toLowerCase()}` ]
        },
    },
    methods: {
        async updateSeverity() {
            if (this.isComponentLevelChange) {
                // For component-level changes, update ALL versions
                const allComponentIds = this.severityChange.componentDetails.allComponentIds;

                const updatePromises = allComponentIds.map(componentId => {
                    const data = {
                        cve_name: this.severityChange.cve_name,
                        component_id: componentId,
                        version: null, // null indicates all versions for this component
                        comment: this.miraComment,
                        cvss3_vector: this.miraCVSS3Vector,
                        severity_id: this.miraSeverity?.id,
                        // For component-level changes, don't specify image_id or artifact_id
                        // to update all artifacts
                    };
                    return this.$axios.post('/api/severity_change/', data);
                });

                try {
                    await Promise.all(updatePromises);
                    this.$parent.updateCVEInfo();
                    this.$emit('close');
                } catch (error) {
                    console.error('Error updating severity for all versions:', error);
                    // Still close and refresh even if some failed
                    this.$parent.updateCVEInfo();
                    this.$emit('close');
                }
            } else {
                // For version and artifact level changes, use existing logic
                const data = {
                    cve_name: this.severityChange.cve_name,
                    component_id: this.severityChange.componentDetails?.id,
                    version: this.severityChange.componentDetails?.version,
                    comment: this.miraComment,
                    cvss3_vector: this.miraCVSS3Vector,
                    severity_id: this.miraSeverity?.id,
                    image_id: this.severityChange?.component?.image?.id,
                    artifact_id: this.severityChange?.component?.artifact_id
                };
                await this.$axios.post('/api/severity_change/', data)
                .then(() => {
                    this.$parent.updateCVEInfo();
                    this.$emit('close');
                })
                .catch(({response}) => { console.error(response) });
            }
        },
        getCVSSSeverity(score) {
            const cvss_severities = ['Low', 'Low', 'Low', 'Low', 'Medium', 'Medium', 'Medium', 'High', 'High', 'Critical', 'Critical']
            const index = parseInt(score.split('.')[0])
            return cvss_severities[index]
        },
        severityCVSSClass(score) {
            let severity = 'none'
            if (score != 'N/A') {
                severity = this.getCVSSSeverity(score).toLowerCase()
            }
            return ['sp-severity-label', `sp-severity-label-${severity}` ]
        },
        severityMiraClass(item_id) {
            let severity = this.$store.state.cveInfo.severity || 'none'
            if (item_id == '7') { severity = 'medium' }
            return ['sp-severity-label', `sp-severity-label-${severity.toLowerCase()}` ]
        },
        severityMiraName(item_id) {
            let severity = this.$store.state.cveInfo.severity || 'N/A'
            if (item_id == '7') { severity = 'medium' }
            return severity
        },
    },
    mounted() {
        this.$refs.severityChange.focus()
    },

}
</script>

<style scoped>
.sp-modal-mask {
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: table;
    transition: opacity 0.3s ease;
}
.sp-modal-wrapper {
    display: table-cell;
    vertical-align: middle;
}
.sp-modal-container {
    width: 400px;
/*    height: 200px;*/
    margin: 0px auto;
    border: 1px solid var(--color-border-header);
    border-radius: 3px;
    background-color: var(--color-bg);
    color: var(--color-fg);
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    font-family: 'Source Sans Pro','Helvetica Neue',Helvetica,Arial,sans-serif;
    font-size: 14px;
}
.sp-modal-header {
    height: 40px;
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 10px;
    font-weight: bold;
    color: var(--color-fg-header);
    background-color: var(--color-bg-header);
    border-top: 1px solid var(--color-border-header);
    border-bottom: 1px solid var(--color-border-header);
}
.sp-modal-header-closebtn {
    cursor: pointer;
}
.sp-modal-content {
    margin: 20px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: calc(100% - 80px);
}
.sp-modal-content>input, .sp-modal-content>textarea, .sp-modal-submit>input {
    background-color: var(--vs-search-input-bg);
    color: var(--color-fg);
    font-size: var(--vs-font-size);
    padding: 0 3px 0 6px;
    border: 1px solid var(--vs-border-color) !important;
    border-radius: 5px;
    height: 32px;
}
.sp-modal-content>textarea {
    margin-top: 10px;
    padding: 5px 3px 0 6px;
    height: 76px;
}
.sp-modal-content>input::placeholder, .sp-modal-content>textarea::placeholder {
    color: var(--vs-search-input-color);
}
.sp-modal-content>input:focus-visible, .sp-modal-content>textarea:focus-visible {
    outline: unset;
}
.sp-modal-severity {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}
.sp-modal-severity .v-select {
    width: 140px;
}
.sp-modal-submit {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
}
.sp-modal-submit>div {
    color: var(--color-severity-high);

}
.sp-modal-submit>input[type="submit"] {
    width: 10em;
    align-self: end;
    cursor: pointer;
}

/* transition */

.sp-modal-enter-from {
    opacity: 0;
}
.sp-modal-leave-to {
    opacity: 0;
}
/*.sp-modal-enter-from .sp-modal-container,
.sp-modal-leave-to .sp-modal-container {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
}*/

.sp-modal-artifact-severity, .sp-modal-component-info {
    margin-bottom: 10px;
    padding: 8px;
    background-color: var(--color-bg-surface);
    border-radius: 3px;
    font-size: 13px;
}

.all-versions-indicator {
    font-style: italic;
    color: var(--color-link);
}

</style>
